import { number, optionalNumber, string, z, zenum, zodLess, ZodLessInfer } from '@kaiizen/zod-less';

/** A raw scan's point (meters, map-style). The model's own points are `Point` (feet, plan). */
export const PointSchema = zodLess({
  x: number.describe('X on the floor plane'),
  y: number.describe('Y on the floor plane'),
}, { description: 'A point on the floor plane, in meters (map-style: x = easting, y = northing).' });

export const ConfidenceSchema = zenum('high', 'medium', 'low', 'unknown');
export type Confidence = ZodLessInfer<typeof ConfidenceSchema>;

export const SurfaceSchema = zodLess({
  id: string.describe('Unique identifier.'),
  start: PointSchema.describe('Start of the bottom edge on the floor plane.'),
  end: PointSchema.describe('End of the bottom edge on the floor plane.'),
  baseHeight: number.describe("Altitude of the bottom edge (0 for walls; a window's sill height)."),
  height: number.describe('Vertical extent. The top edge sits at baseHeight + height.'),
  confidence: ConfidenceSchema.describe('Detection confidence.'),
}, {
  description:
    "A flat linear element of the room — wall, door, window, or opening. Every surface is a rectangle standing perpendicular to the floor: start/end trace its bottom edge on the floor plane, baseHeight lifts that edge off the floor, and height extends it upward. Length is not stored; it's hypot(end.x − start.x, end.y − start.y).",
});
export type Surface = ZodLessInfer<typeof SurfaceSchema>;

export const RoomObjectSchema = zodLess({
  id: string.describe('Unique identifier.'),
  category: string.describe('Object category (e.g. bed, storage, table).'),
  position: PointSchema.describe("Center of the box's footprint on the floor plane."),
  baseHeight: number.describe("Altitude of the box's bottom face (0 for anything resting on the floor)."),
  length: number.describe("Extent along the box's local x (heading) axis."),
  width: number.describe("Extent along the box's local y axis."),
  height: number.describe('Vertical extent. The top face sits at baseHeight + height.'),
  rotation: number.describe('Heading of the length axis: radians, CCW from +x, viewed from above.'),
  confidence: ConfidenceSchema.describe('Detection confidence.'),
}, {
  description: 'A piece of furniture or fixture: an axis-defined rectangular prism (box), rotated about the vertical axis and placed in 3D.',
});
export type RoomObject = ZodLessInfer<typeof RoomObjectSchema>;

const SurfaceArraySchema = zodLess([SurfaceSchema]);
const RoomObjectArraySchema = zodLess([RoomObjectSchema]);
export const UnitsSchema = zodLess('meters').describe('Linear units; v2 is always meters.');
export const SchemaVersionSchema = z.literal(2).describe('Only v2 is supported.');

export const RawFloorPlanSchema = zodLess({
  schemaVersion: SchemaVersionSchema,
  units: UnitsSchema,
  walls: SurfaceArraySchema.describe('Perimeter wall segments.'),
  doors: SurfaceArraySchema.describe('Door openings.'),
  windows: SurfaceArraySchema.describe('Window openings.'),
  openings: SurfaceArraySchema.describe('Pass-through openings (no door leaf).'),
  objects: RoomObjectArraySchema.describe('Furniture and fixtures detected in the room.'),
  magneticNorthAngle: optionalNumber.describe(
    "Bearing of MAGNETIC north in the floor plane: radians, measured CLOCKWISE from +y — a standard compass bearing once the plan is drawn with +y up. 0 means north is straight up the map; π/2 means north points along +x. The (x, y) unit vector toward north, if ever needed, is (sin a, cos a). A weighted circular mean of Core Motion attitude samples (magnetic-north referenced, tilt-independent) paired with the ARKit camera pose across the whole scan path, gated on magnetometer calibration and rotation speed; absent when no reliable samples were available. As produced by the native module this is in the raw capture frame; after normalizeFloorPlan it's the residual within ±45° (the cardinal snap puts north up). Magnetic north, NOT true north — once we capture lat/long we can offset by magnetic declination. RoomPlan's world frame is only gravity-aligned, so without this the model carries no notion of north.",
  ),
  magneticNorthSpreadDeg: optionalNumber.describe(
    'Circular standard deviation, in degrees, of the samples behind magneticNorthAngle. Rotation-invariant, so it survives normalization unchanged. Roughly: ≲5° means the compass was consistent across the scan; ≳15° means treat the heading as a hint.',
  ),
}, {
  description:
    'RawFloorPlan schema v2 — map-style coordinates. Right-handed, z-up, in meters: x/y span the floor plane, z is altitude above the floor. Angles are radians, CCW from +x viewed from above — except magneticNorthAngle, which is a compass-style bearing.',
});
export type RawFloorPlan = ZodLessInfer<typeof RawFloorPlanSchema>;
